import { useEffect, useRef, useState, useCallback } from "react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import {
    Loader2,
    Plus,
    Trash2,
    Crosshair,
    Save,
    Check,
    ZoomIn,
    ZoomOut,
    RotateCcw,
} from "lucide-react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 10;
const DEFAULT_CATEGORY = {
    slug: "general",
    label: "Lokalizacja",
    color: "#facc15",
    icon: "",
};

const CATEGORIES = [
    { id: "general", label: "Lokalizacja" },
    { id: "firma", label: "Firma" },
    { id: "stacja", label: "Stacja paliw" },
    { id: "ciekawe", label: "Ciekawe miejsce" },
];

export default function MapManager() {
    const [pois, setPois] = useState([]);
    const [modInfo, setModInfo] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // Per-POI unsaved size edits (id -> size). Lets us show a live preview on
    // the map and a clear "save" action without losing the persisted value.
    const [sizeEdits, setSizeEdits] = useState({});

    const [picking, setPicking] = useState(false);
    const [draft, setDraft] = useState({
        title: "",
        description: "",
        category: "general",
        x: 50,
        y: 50,
        image_url: "",
        size: 14,
    });
    const [poiImageFile, setPoiImageFile] = useState(null);
    const [savingMap, setSavingMap] = useState(false);
    const [newMapUrl, setNewMapUrl] = useState("");
    const fileRef = useRef(null);

    // ===== Zoom & Pan state =====
    const containerRef = useRef(null);
    const transformRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
    const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
    const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0, moved: false });

    // ===== POI marker drag state =====
    // draggingPoiId triggers the global mousemove/mouseup effect; the actual
    // mutable drag info lives in dragPoiRef so we can read current values
    // inside the effect without re-subscribing on every position change.
    const [draggingPoiId, setDraggingPoiId] = useState(null);
    const dragPoiRef = useRef(null);

    // Track container dimensions so markers can be positioned in a non-scaled
    // overlay layer (keeps them crisp and constant-size at any zoom level).
    useEffect(() => {
        const c = containerRef.current;
        if (!c) return;
        const update = () =>
            setContainerSize({ w: c.clientWidth, h: c.clientHeight });
        update();
        const ro = new ResizeObserver(update);
        ro.observe(c);
        return () => ro.disconnect();
    }, []);

    // Displayed image rect inside the container for an object-contain image.
    // Markers AND click-to-pick use this rect so coordinates map to the same
    // map point as the public site, regardless of container aspect ratio.
    const getImageRect = useCallback(() => {
        const { w: cw, h: ch } = containerSize;
        const { w: iw, h: ih } = imgNatural;
        if (!cw || !ch || !iw || !ih) {
            return { ox: 0, oy: 0, dw: cw, dh: ch };
        }
        const scale = Math.min(cw / iw, ch / ih);
        const dw = iw * scale;
        const dh = ih * scale;
        return { ox: (cw - dw) / 2, oy: (ch - dh) / 2, dw, dh };
    }, [containerSize, imgNatural]);

    const clampPan = useCallback((px, py, z) => {
        const c = containerRef.current;
        if (!c) return { x: px, y: py };
        const w = c.clientWidth;
        const h = c.clientHeight;
        const minX = w * (1 - z);
        const minY = h * (1 - z);
        return {
            x: Math.min(0, Math.max(minX, px)),
            y: Math.min(0, Math.max(minY, py)),
        };
    }, []);

    const setZoomAt = useCallback(
        (newZoom, anchorX, anchorY) => {
            const c = containerRef.current;
            if (!c) return;
            const clampedZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, newZoom));
            if (clampedZoom === zoom) return;
            const ax = anchorX ?? c.clientWidth / 2;
            const ay = anchorY ?? c.clientHeight / 2;
            const ratio = clampedZoom / zoom;
            const newPanX = ax - (ax - pan.x) * ratio;
            const newPanY = ay - (ay - pan.y) * ratio;
            setPan(clampPan(newPanX, newPanY, clampedZoom));
            setZoom(clampedZoom);
        },
        [zoom, pan, clampPan]
    );

    const zoomIn = () => setZoomAt(zoom * 1.4);
    const zoomOut = () => setZoomAt(zoom / 1.4);
    const resetView = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    // Wheel zoom (anchored at cursor). Attached natively for { passive: false }.
    useEffect(() => {
        const c = containerRef.current;
        if (!c) return;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = c.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;
            const factor = Math.exp(-e.deltaY * 0.0015);
            setZoomAt(zoom * factor, mx, my);
        };
        c.addEventListener("wheel", onWheel, { passive: false });
        return () => c.removeEventListener("wheel", onWheel);
    }, [zoom, setZoomAt]);

    // Global mouseup to end drag even if released outside the map
    useEffect(() => {
        const onUp = () => setDragging(false);
        window.addEventListener("mouseup", onUp);
        return () => window.removeEventListener("mouseup", onUp);
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const [pRes, mRes, cRes] = await Promise.all([
                api.get("/poi"),
                api.get("/mod-info"),
                api.get("/poi-categories"),
            ]);
            setPois(pRes.data);
            setModInfo(mRes.data);
            setCategories(cRes.data);
            setNewMapUrl(mRes.data.map_image_url || "");
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const onMapClick = (e) => {
        if (!picking) return;
        if (dragStart.current.moved) {
            // ignore click that ended a drag
            dragStart.current.moved = false;
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        // Fraction across the (scaled) container, 0..1
        const fx = (e.clientX - rect.left) / rect.width;
        const fy = (e.clientY - rect.top) / rect.height;
        const { ox, oy, dw, dh } = getImageRect();
        const cw = containerSize.w || rect.width;
        const ch = containerSize.h || rect.height;
        // Convert to a percentage of the actual displayed image rect
        const x = dw ? ((fx * cw - ox) / dw) * 100 : fx * 100;
        const y = dh ? ((fy * ch - oy) / dh) * 100 : fy * 100;
        setDraft((d) => ({
            ...d,
            x: Math.max(0, Math.min(100, x)),
            y: Math.max(0, Math.min(100, y)),
        }));
        setPicking(false);
    };

    const onMapMouseDown = (e) => {
        if (e.button !== 0) return;
        // Allow panning in both modes; we filter clicks by dragStart.moved
        setDragging(true);
        dragStart.current = {
            x: e.clientX,
            y: e.clientY,
            panX: pan.x,
            panY: pan.y,
            moved: false,
        };
    };

    const onMapMouseMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        if (!dragStart.current.moved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
            dragStart.current.moved = true;
        }
        if (dragStart.current.moved) {
            setPan(
                clampPan(
                    dragStart.current.panX + dx,
                    dragStart.current.panY + dy,
                    zoom
                )
            );
        }
    };

    const onMapMouseUp = () => {
        setDragging(false);
    };

    // ===== POI marker drag handlers =====
    const onMarkerMouseDown = (e, p) => {
        if (picking) return; // in pick mode, let map handle the click
        if (e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        dragPoiRef.current = {
            id: p.id,
            startMouseX: e.clientX,
            startMouseY: e.clientY,
            startX: p.x,
            startY: p.y,
            currentX: p.x,
            currentY: p.y,
            moved: false,
        };
        setDraggingPoiId(p.id);
    };

    // Global mousemove/mouseup while a POI is being dragged. We re-bind
    // whenever zoom / image rect changes so the conversion stays correct.
    useEffect(() => {
        if (!draggingPoiId) return;
        const onMove = (e) => {
            const d = dragPoiRef.current;
            if (!d) return;
            const { dw, dh } = getImageRect();
            if (!dw || !dh) return;
            // Screen pixel delta -> percentage of the displayed map image.
            // Displayed image on screen is zoom * dw wide / zoom * dh tall.
            const dxPct = ((e.clientX - d.startMouseX) / (zoom * dw)) * 100;
            const dyPct = ((e.clientY - d.startMouseY) / (zoom * dh)) * 100;
            if (!d.moved && (Math.abs(dxPct) > 0.05 || Math.abs(dyPct) > 0.05)) {
                d.moved = true;
            }
            const nx = Math.max(0, Math.min(100, d.startX + dxPct));
            const ny = Math.max(0, Math.min(100, d.startY + dyPct));
            d.currentX = nx;
            d.currentY = ny;
            setPois((arr) =>
                arr.map((p) => (p.id === d.id ? { ...p, x: nx, y: ny } : p))
            );
        };
        const onUp = async () => {
            const d = dragPoiRef.current;
            dragPoiRef.current = null;
            setDraggingPoiId(null);
            if (!d) return;
            if (!d.moved) return;
            try {
                await api.put(`/poi/${d.id}`, { x: d.currentX, y: d.currentY });
            } catch (err) {
                setError(formatApiErrorDetail(err.response?.data?.detail));
                // Revert on failure
                setPois((arr) =>
                    arr.map((p) =>
                        p.id === d.id ? { ...p, x: d.startX, y: d.startY } : p
                    )
                );
            }
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [draggingPoiId, zoom, getImageRect]);

    const addPoi = async () => {
        if (!draft.title.trim()) {
            setError("Podaj tytuł punktu.");
            return;
        }
        try {
            let payload = { ...draft };
            if (poiImageFile) {
                const fd = new FormData();
                fd.append("file", poiImageFile);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                payload.image_url = up.data.url;
            }
            await api.post("/poi", payload);
            setDraft({
                title: "",
                description: "",
                category: "general",
                x: 50,
                y: 50,
                image_url: "",
                size: 14,
            });
            setPoiImageFile(null);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const deletePoi = async (id) => {
        if (!window.confirm("Usunąć punkt?")) return;
        try {
            await api.delete(`/poi/${id}`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const updatePoiSize = async (id, newSize) => {
        try {
            await api.put(`/poi/${id}`, { size: newSize });
            setPois((arr) =>
                arr.map((p) => (p.id === id ? { ...p, size: newSize } : p))
            );
            setSizeEdits((m) => {
                const next = { ...m };
                delete next[id];
                return next;
            });
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const updatePoiCategory = async (id, newSlug) => {
        try {
            await api.put(`/poi/${id}`, { category: newSlug });
            setPois((arr) =>
                arr.map((p) => (p.id === id ? { ...p, category: newSlug } : p))
            );
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const saveMapImage = async () => {
        setSavingMap(true);
        setError("");
        try {
            let urlToSave = newMapUrl;
            const file = fileRef.current?.files?.[0];
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                const up = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                urlToSave = up.data.url;
            }
            await api.put("/mod-info", { map_image_url: urlToSave });
            await load();
            if (fileRef.current) fileRef.current.value = "";
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSavingMap(false);
        }
    };

    const mapSrc = modInfo?.map_image_url
        ? resolveMediaUrl(modInfo.map_image_url)
        : "https://static.prod-images.emergentagent.com/jobs/359e1a58-6c2d-46ea-8fb3-26c1f78309e7/images/3731fdb140d58211a26e9163309a47c5bf2fa9c3c2956d688b47d564407e2299.png";

    const categoryMap = categories.reduce((acc, c) => {
        acc[c.slug] = c;
        return acc;
    }, {});
    const getCategory = (slug) => categoryMap[slug] || DEFAULT_CATEGORY;
    const draftCategory = getCategory(draft.category);

    return (
        <div data-testid="admin-map" className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-6">
                <div className="panel-yellow p-4">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <div className="label-eyebrow">Mapa</div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 border border-white/10 rounded-md p-1 bg-[color:var(--bg-base)]">
                                <button
                                    type="button"
                                    data-testid="map-zoom-out"
                                    onClick={zoomOut}
                                    disabled={zoom <= MIN_ZOOM + 0.001}
                                    title="Pomniejsz"
                                    className="p-1.5 rounded text-[color:var(--text-secondary)] hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ZoomOut size={14} />
                                </button>
                                <div
                                    data-testid="map-zoom-level"
                                    className="text-[10px] uppercase tracking-wider text-[color:var(--text-secondary)] min-w-[3rem] text-center"
                                >
                                    {Math.round(zoom * 100)}%
                                </div>
                                <button
                                    type="button"
                                    data-testid="map-zoom-in"
                                    onClick={zoomIn}
                                    disabled={zoom >= MAX_ZOOM - 0.001}
                                    title="Powiększ"
                                    className="p-1.5 rounded text-[color:var(--text-secondary)] hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <ZoomIn size={14} />
                                </button>
                                <button
                                    type="button"
                                    data-testid="map-zoom-reset"
                                    onClick={resetView}
                                    disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                                    title="Resetuj widok"
                                    className="p-1.5 rounded text-[color:var(--text-secondary)] hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <RotateCcw size={14} />
                                </button>
                            </div>
                            <button
                                data-testid="map-pick-toggle"
                                onClick={() => setPicking((v) => !v)}
                                className={`text-xs uppercase tracking-wider px-3 py-2 rounded-md inline-flex items-center gap-2 border ${
                                    picking
                                        ? "bg-[color:var(--primary)] text-[color:var(--bg-base)] border-[color:var(--primary)]"
                                        : "border-white/10 text-[color:var(--text-secondary)] hover:text-white"
                                }`}
                            >
                                <Crosshair size={14} />
                                {picking ? "Kliknij na mapę..." : "Wybierz pozycję klikiem"}
                            </button>
                        </div>
                    </div>
                    <div
                        ref={containerRef}
                        data-testid="admin-map-image"
                        onMouseDown={onMapMouseDown}
                        onMouseMove={onMapMouseMove}
                        onMouseUp={onMapMouseUp}
                        onMouseLeave={onMapMouseUp}
                        className={`relative w-full aspect-[16/10] bg-[color:var(--bg-base)] overflow-hidden rounded-md border border-white/10 select-none touch-none ${
                            dragging
                                ? "cursor-grabbing"
                                : picking
                                ? "cursor-crosshair"
                                : zoom > 1
                                ? "cursor-grab"
                                : "cursor-default"
                        }`}
                    >
                        <div
                            ref={transformRef}
                            onClick={onMapClick}
                            className="absolute inset-0"
                            style={{
                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                transformOrigin: "0 0",
                                willChange: "transform",
                            }}
                        >
                            <img
                                src={mapSrc}
                                alt="Mapa"
                                draggable={false}
                                onLoad={(e) =>
                                    setImgNatural({
                                        w: e.target.naturalWidth,
                                        h: e.target.naturalHeight,
                                    })
                                }
                                className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                            />
                        </div>

                        {/* Markers in a separate, NON-scaled overlay so they
                            stay crisp and keep a constant pixel size at any
                            zoom. In pick mode they're click-through so the
                            map click handler below receives the event;
                            otherwise they're draggable to edit position. */}
                        <div className="absolute inset-0 pointer-events-none">
                            {pois.map((p) => {
                                const cat = getCategory(p.category);
                                const sz =
                                    (sizeEdits[p.id] ?? p.size ?? 14) *
                                    Math.min(3.5, Math.sqrt(zoom));
                                const { ox, oy, dw, dh } = getImageRect();
                                const left =
                                    pan.x + zoom * (ox + (p.x / 100) * dw);
                                const top =
                                    pan.y + zoom * (oy + (p.y / 100) * dh);
                                const isDragging = draggingPoiId === p.id;
                                return (
                                    <div
                                        key={p.id}
                                        data-testid={`admin-marker-${p.id}`}
                                        onMouseDown={(e) => onMarkerMouseDown(e, p)}
                                        className={`absolute rounded-full border-2 border-white flex items-center justify-center shadow-md select-none ${
                                            picking
                                                ? "pointer-events-none"
                                                : isDragging
                                                ? "pointer-events-auto cursor-grabbing ring-2 ring-[color:var(--primary)] z-10"
                                                : "pointer-events-auto cursor-grab hover:ring-2 hover:ring-white/60"
                                        }`}
                                        style={{
                                            left: `${left}px`,
                                            top: `${top}px`,
                                            background: cat.color,
                                            color: "#000",
                                            width: `${sz}px`,
                                            height: `${sz}px`,
                                            fontSize: `${Math.max(8, sz * 0.6)}px`,
                                            transform: "translate(-50%, -50%)",
                                            lineHeight: 1,
                                        }}
                                        title={`${p.title} - przeciągnij, aby zmienić pozycję`}
                                    >
                                        {cat.icon}
                                    </div>
                                );
                            })}
                            <div
                                className="absolute rounded-full border-2 border-white flex items-center justify-center pointer-events-none ring-2 ring-white/40"
                                style={{
                                    left: `${pan.x + zoom * (getImageRect().ox + (draft.x / 100) * getImageRect().dw)}px`,
                                    top: `${pan.y + zoom * (getImageRect().oy + (draft.y / 100) * getImageRect().dh)}px`,
                                    background: draftCategory.color,
                                    color: "#000",
                                    width: `${(draft.size || 14) * Math.min(3.5, Math.sqrt(zoom))}px`,
                                    height: `${(draft.size || 14) * Math.min(3.5, Math.sqrt(zoom))}px`,
                                    fontSize: `${Math.max(8, (draft.size || 14) * Math.min(3.5, Math.sqrt(zoom)) * 0.6)}px`,
                                    transform: "translate(-50%, -50%)",
                                    lineHeight: 1,
                                }}
                                data-testid="draft-marker"
                            >
                                {draftCategory.icon}
                            </div>
                        </div>
                    </div>
                    <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-muted)]">
                        Scroll = zoom · Przeciągnij mapę = przesuń · Chwyć marker = edytuj pozycję · Tryb wyboru = klik stawia punkt
                    </div>
                </div>

                <div className="panel-yellow p-6">
                    <div className="label-eyebrow mb-3">Obraz mapy</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                                URL obrazu
                            </label>
                            <input
                                data-testid="map-image-url-input"
                                value={newMapUrl}
                                onChange={(e) => setNewMapUrl(e.target.value)}
                                className="w-full bg-[color:var(--bg-base)] border border-white/10 rounded-md px-3 py-2 text-white text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                                Lub prześlij plik
                            </label>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                data-testid="map-image-file-input"
                                className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                            />
                        </div>
                    </div>
                    <button
                        data-testid="map-save-image-btn"
                        onClick={saveMapImage}
                        disabled={savingMap}
                        className="btn-primary mt-4"
                    >
                        {savingMap ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                        Zapisz mapę
                    </button>
                </div>
            </div>

            <div className="lg:col-span-4 space-y-6">
                <div className="panel-yellow p-6">
                    <div className="label-eyebrow mb-3">Nowy punkt</div>
                    <Field label="Tytuł">
                        <input
                            data-testid="poi-title-input"
                            value={draft.title}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, title: e.target.value }))
                            }
                            className="input"
                        />
                    </Field>
                    <Field label="Opis">
                        <textarea
                            data-testid="poi-description-input"
                            value={draft.description}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, description: e.target.value }))
                            }
                            rows={3}
                            className="input"
                        />
                    </Field>
                    <Field label="Kategoria">
                        <select
                            data-testid="poi-category-select"
                            value={draft.category}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, category: e.target.value }))
                            }
                            className="input"
                        >
                            {(categories.length ? categories : [DEFAULT_CATEGORY]).map((c) => (
                                <option key={c.slug} value={c.slug}>
                                    {c.icon ? `${c.icon} ` : ""}{c.label}
                                </option>
                            ))}
                        </select>
                        <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
                            <span
                                className="inline-block w-3 h-3 rounded-full border border-white/40"
                                style={{ background: draftCategory.color }}
                            />
                            {draftCategory.color}
                        </div>
                    </Field>
                    <Field label="URL zdjęcia (opcjonalnie)">
                        <input
                            data-testid="poi-image-url-input"
                            value={draft.image_url}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, image_url: e.target.value }))
                            }
                            placeholder="https://..."
                            className="input"
                        />
                    </Field>
                    <Field label="Lub prześlij zdjęcie">
                        <input
                            type="file"
                            accept="image/*"
                            data-testid="poi-image-file-input"
                            onChange={(e) => setPoiImageFile(e.target.files?.[0] || null)}
                            className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="X (%)">
                            <input
                                type="number"
                                step="0.1"
                                data-testid="poi-x-input"
                                value={draft.x}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        x: parseFloat(e.target.value || "0"),
                                    }))
                                }
                                className="input"
                            />
                        </Field>
                        <Field label="Y (%)">
                            <input
                                type="number"
                                step="0.1"
                                data-testid="poi-y-input"
                                value={draft.y}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        y: parseFloat(e.target.value || "0"),
                                    }))
                                }
                                className="input"
                            />
                        </Field>
                    </div>
                    <Field label={`Rozmiar markera: ${draft.size || 14}px`}>
                        <input
                            type="range"
                            min="8"
                            max="40"
                            step="1"
                            data-testid="poi-size-input"
                            value={draft.size || 14}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    size: parseInt(e.target.value || "14", 10),
                                }))
                            }
                            className="w-full accent-[color:var(--primary)]"
                        />
                    </Field>
                    {error && (
                        <div className="text-sm text-red-400 mb-3" data-testid="poi-error">
                            {error}
                        </div>
                    )}
                    <button
                        data-testid="poi-add-btn"
                        onClick={addPoi}
                        className="btn-primary w-full mt-2"
                    >
                        <Plus size={14} /> Dodaj punkt
                    </button>
                    <style>{`
                        .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                        .input:focus { outline:none; border-color: var(--primary); }
                    `}</style>
                </div>

                <div className="panel-yellow p-6 max-h-[420px] overflow-y-auto">
                    <div className="label-eyebrow mb-3">Punkty ({pois.length})</div>
                    {loading ? (
                        <Loader2 className="animate-spin text-[color:var(--text-secondary)]" />
                    ) : pois.length === 0 ? (
                        <div className="text-sm text-[color:var(--text-secondary)]">
                            Brak punktów.
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {pois.map((p) => {
                                const pc = getCategory(p.category);
                                const sz = p.size || 14;
                                return (
                                    <li
                                        key={p.id}
                                        data-testid={`poi-row-${p.id}`}
                                        className="flex items-start gap-2 p-3 border border-white/5 rounded-md"
                                    >
                                        <div
                                            className="rounded-full border-2 border-white flex items-center justify-center text-[10px] shrink-0 mt-0.5"
                                            style={{
                                                background: pc.color,
                                                color: "#000",
                                                width: 22,
                                                height: 22,
                                            }}
                                        >
                                            {pc.icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-white font-medium truncate">
                                                {p.title}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mt-0.5">
                                                {pc.label} · {p.x.toFixed(1)}, {p.y.toFixed(1)}
                                            </div>
                                            <div className="flex items-center gap-2 mt-2">
                                                <select
                                                    data-testid={`poi-cat-select-${p.id}`}
                                                    value={p.category}
                                                    onChange={(e) =>
                                                        updatePoiCategory(p.id, e.target.value)
                                                    }
                                                    className="bg-[color:var(--bg-base)] border border-white/10 rounded text-[10px] text-white px-1.5 py-1 flex-1 min-w-0"
                                                >
                                                    {(categories.length ? categories : [DEFAULT_CATEGORY]).map(
                                                        (c) => (
                                                            <option key={c.slug} value={c.slug}>
                                                                {c.icon ? `${c.icon} ` : ""}{c.label}
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                                {(() => {
                                                    const editVal = sizeEdits[p.id] ?? sz;
                                                    const dirty = editVal !== sz;
                                                    return (
                                                        <>
                                                            <input
                                                                type="number"
                                                                min="8"
                                                                max="40"
                                                                data-testid={`poi-size-input-${p.id}`}
                                                                value={editVal}
                                                                onChange={(e) => {
                                                                    const v = Math.max(
                                                                        8,
                                                                        Math.min(40, parseInt(e.target.value || "14", 10))
                                                                    );
                                                                    setSizeEdits((m) => ({ ...m, [p.id]: v }));
                                                                }}
                                                                className="bg-[color:var(--bg-base)] border border-white/10 rounded text-[10px] text-white px-1.5 py-1 w-14"
                                                                title="Rozmiar markera (px)"
                                                            />
                                                            <span className="text-[9px] uppercase tracking-wider text-[color:var(--text-muted)]">
                                                                px
                                                            </span>
                                                            <button
                                                                type="button"
                                                                data-testid={`poi-size-save-${p.id}`}
                                                                onClick={() => updatePoiSize(p.id, editVal)}
                                                                disabled={!dirty}
                                                                title={dirty ? "Zapisz rozmiar" : "Zapisano"}
                                                                className={`p-1 rounded shrink-0 ${
                                                                    dirty
                                                                        ? "text-[color:var(--bg-base)] bg-[color:var(--primary)] hover:opacity-90"
                                                                        : "text-[color:var(--text-muted)] bg-white/5 cursor-default"
                                                                }`}
                                                            >
                                                                <Check size={12} strokeWidth={3} />
                                                            </button>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <button
                                            data-testid={`poi-delete-${p.id}`}
                                            onClick={() => deletePoi(p.id)}
                                            className="text-red-400 hover:text-red-300 p-1"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-3">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
